import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  CalendarDays, 
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Paperclip,
  Eye,
  User,
  FileText,
  Check,
  X,
  Filter,
  RefreshCw
} from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatUtcDate } from '@/utils/dateUtils';

function AdminLeaveManagementPage() {
  const { t, isRTL } = useLanguage();
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState('');
  const [adminRemarks, setAdminRemarks] = useState('');
  const [isPaid, setIsPaid] = useState(null); // true | false | null
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  
  // Filters
  const [filterDepartment, setFilterDepartment] = useState('');
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    fetchLeaveRequests();
  }, [activeTab, filterDepartment]);

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchLeaveRequests = async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeTab !== 'all') {
        params.status = activeTab;
      }
      if (filterDepartment && filterDepartment !== 'all') {
        params.department = filterDepartment;
      }
      
      const response = await axios.get(`${API}/admin/leave-requests`, {
        params,
        withCredentials: true
      });
      setLeaveRequests(response.data.leave_requests || []);
      setCounts(response.data.counts || { pending: 0, approved: 0, rejected: 0 });
    } catch (error) {
      console.error('Failed to fetch leave requests:', error);
      showMessage('error', 'Failed to load leave requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/admin/departments`, {
        withCredentials: true
      });
      setDepartments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleAction = async () => {
    if (!selectedRequest || !actionType) return;
    if (actionType === 'approve' && isPaid === null) {
      showMessage('error', 'Please choose paid or unpaid salary for this leave');
      return;
    }
    
    setProcessing(true);
    try {
      const payload = {
        action: actionType,
        admin_remarks: adminRemarks || null,
      };
      if (actionType === 'approve') {
        payload.is_paid = isPaid;
      }
      await axios.put(
        `${API}/admin/leave-request/${selectedRequest.leave_id}`,
        payload,
        { withCredentials: true }
      );
      
      const paidLabel = actionType === 'approve'
        ? (isPaid ? ' (paid salary)' : ' (unpaid salary)')
        : '';
      showMessage('success', `Leave request ${actionType}d successfully${paidLabel}`);
      setShowActionDialog(false);
      setShowDetailDialog(false);
      setAdminRemarks('');
      setIsPaid(null);
      fetchLeaveRequests();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || `Failed to ${actionType} request`);
    } finally {
      setProcessing(false);
    }
  };

  const openActionDialog = (request, action) => {
    setSelectedRequest(request);
    setActionType(action);
    setAdminRemarks('');
    // Default: off_day / unpaid types → unpaid; others → paid (admin can change)
    if (action === 'approve') {
      const unpaidTypes = ['off_day', 'unpaid'];
      setIsPaid(unpaidTypes.includes(request.leave_type) ? false : true);
    } else {
      setIsPaid(null);
    }
    setShowActionDialog(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-300"><CheckCircle className="w-3 h-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getLeaveTypeBadge = (type) => {
    const types = {
      'off_day': { label: 'Off Day', color: 'bg-blue-50 text-blue-700' },
      'sick_leave': { label: 'Sick Leave', color: 'bg-red-50 text-red-700' },
      'vacation': { label: 'Vacation', color: 'bg-purple-50 text-purple-700' },
      'personal': { label: 'Personal', color: 'bg-gray-50 text-gray-700' },
      'emergency': { label: 'Emergency', color: 'bg-orange-50 text-orange-700' }
    };
    const config = types[type] || { label: type, color: 'bg-gray-50 text-gray-700' };
    return <Badge variant="outline" className={config.color}>{config.label}</Badge>;
  };

  const getSalaryBadge = (request) => {
    if (request.status !== 'approved' || request.is_paid == null) return null;
    return request.is_paid ? (
      <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">Paid</Badge>
    ) : (
      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Unpaid</Badge>
    );
  };

  const formatDate = (dateStr) => {
    return formatUtcDate(dateStr + 'T00:00:00');
  };

  const renderTable = (requests) => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Staff</TableHead>
          <TableHead>Subject</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Date Range</TableHead>
          <TableHead>Days</TableHead>
          <TableHead>{t("common.status")}</TableHead>
          <TableHead>{t("common.actions")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {requests.map((request) => (
          <TableRow key={request.leave_id}>
            <TableCell>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-purple-50 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-sm">{request.staff_name}</p>
                  <p className="text-xs text-gray-500">{request.staff_department || 'No Dept'}</p>
                </div>
              </div>
            </TableCell>
            <TableCell className="font-medium max-w-[200px] truncate">{request.subject}</TableCell>
            <TableCell>
              <div className="flex flex-col gap-1">
                {getLeaveTypeBadge(request.leave_type)}
                {getSalaryBadge(request)}
              </div>
            </TableCell>
            <TableCell>
              <span className="text-sm">
                {formatDate(request.start_date)} - {formatDate(request.end_date)}
              </span>
            </TableCell>
            <TableCell className="text-center">{request.days_count}</TableCell>
            <TableCell>{getStatusBadge(request.status)}</TableCell>
            <TableCell>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedRequest(request);
                    setShowDetailDialog(true);
                  }}
                  data-testid={`view-leave-${request.leave_id}`}
                >
                  <Eye className="w-4 h-4" />
                </Button>
                {request.status === 'pending' && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                      onClick={() => openActionDialog(request, 'approve')}
                      data-testid={`approve-leave-${request.leave_id}`}
                    >
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => openActionDialog(request, 'reject')}
                      data-testid={`reject-leave-${request.leave_id}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <div className="relative pb-8" data-testid="admin-leave-page">
      <div className="pointer-events-none absolute inset-x-0 -top-6 h-56 bg-[radial-gradient(ellipse_at_top,_rgba(99,102,241,0.12),_transparent_60%)]" />

      <div className="relative space-y-5 md:space-y-6">
      {message.text && (
        <div className={`att-fade-up rounded-2xl px-4 py-3 text-sm font-medium ring-1 ${
          message.type === 'error' ? 'bg-rose-50 text-rose-700 ring-rose-200' :
          message.type === 'success' ? 'bg-indigo-50 text-indigo-700 ring-indigo-200' :
          'bg-sky-50 text-sky-700 ring-sky-200'
        }`}>
          {message.text}
        </div>
      )}

      <section className="att-fade-up overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-[0_20px_50px_-28px_rgba(15,23,42,0.35)] backdrop-blur">
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-4 py-4 text-white sm:px-6 sm:py-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{t("leave.title")}</h1>
              <p className="mt-1.5 text-sm text-indigo-50/90">Review and manage staff leave requests</p>
            </div>
            <Button
              variant="ghost"
              onClick={fetchLeaveRequests}
              data-testid="refresh-leave-requests-btn"
              className="h-11 rounded-2xl bg-white/15 text-white ring-1 ring-white/25 hover:bg-white/25"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </section>

      <section className="att-fade-up grid grid-cols-1 gap-3 md:grid-cols-3 md:gap-4" style={{ animationDelay: '60ms' }}>
        {[
          { title: t("common.pending"), value: counts.pending, icon: Clock, soft: 'bg-amber-50 text-amber-700', accent: 'from-amber-500 to-orange-500' },
          { title: t("leave.approved"), value: counts.approved, icon: CheckCircle, soft: 'bg-indigo-50 text-indigo-600', accent: 'from-indigo-500 to-purple-500' },
          { title: t("leave.rejected"), value: counts.rejected, icon: XCircle, soft: 'bg-rose-50 text-rose-600', accent: 'from-rose-500 to-pink-500' },
        ].map((k) => (
          <div key={k.title} className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm transition duration-300 hover:-translate-y-0.5 hover:shadow-lg">
            <div className={`absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br ${k.accent} opacity-10 blur-xl`} />
            <div className="relative flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{k.title}</p>
                <p className="mt-2 text-3xl font-bold tabular-nums text-slate-900">{k.value}</p>
              </div>
              <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${k.soft}`}>
                <k.icon className="h-5 w-5" />
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="att-fade-up overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-sm" style={{ animationDelay: '120ms' }}>
        <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-purple-50 text-purple-600">
                <CalendarDays className="h-4 w-4" />
              </span>
              Leave Requests
            </h3>
            <p className="mt-1 text-sm text-slate-500">View and process staff leave requests</p>
          </div>
          {departments.length > 0 && (
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-400" />
              <Select value={filterDepartment} onValueChange={(v) => { setFilterDepartment(v); }}>
                <SelectTrigger className="h-10 w-[180px] rounded-2xl border-slate-200" data-testid="filter-department-select">
                  <SelectValue placeholder="All Departments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="p-0">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="overflow-x-auto border-b border-slate-100 px-4 py-2">
              <TabsList className="inline-flex h-auto min-w-full gap-1 rounded-2xl bg-slate-100/90 p-1.5 sm:min-w-0">
                <TabsTrigger value="pending" className="rounded-xl data-[state=active]:bg-white data-[state=active]:text-amber-800 data-[state=active]:shadow-sm">
                  Pending ({counts.pending})
                </TabsTrigger>
                <TabsTrigger value="approved" className="rounded-xl data-[state=active]:bg-white data-[state=active]:text-indigo-700 data-[state=active]:shadow-sm">
                  Approved ({counts.approved})
                </TabsTrigger>
                <TabsTrigger value="rejected" className="rounded-xl data-[state=active]:bg-white data-[state=active]:text-rose-700 data-[state=active]:shadow-sm">
                  Rejected ({counts.rejected})
                </TabsTrigger>
                <TabsTrigger value="all" className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm">All</TabsTrigger>
              </TabsList>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
              </div>
            ) : leaveRequests.length === 0 ? (
              <div className="py-16 text-center text-slate-400">
                <CalendarDays className="mx-auto mb-4 h-12 w-12 opacity-40" />
                <p>No leave requests found</p>
              </div>
            ) : (
              <div className="overflow-x-auto p-4">
                {renderTable(leaveRequests)}
              </div>
            )}
          </Tabs>
        </div>
      </section>

      {/* Request Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg overflow-hidden rounded-3xl border-0 p-0 shadow-2xl">
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-6 py-5 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-white">
                <FileText className="h-5 w-5" />
                Leave Request Details
              </DialogTitle>
            </DialogHeader>
          </div>

          {selectedRequest && (
            <div className="space-y-4 px-6 py-5">
              <div className="flex items-center gap-3 rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-100">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-50 text-purple-600">
                  <User className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{selectedRequest.staff_name}</p>
                  <p className="text-sm text-slate-500">
                    {selectedRequest.staff_email} • {selectedRequest.staff_department || 'No Dept'}
                  </p>
                </div>
              </div>

              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">{selectedRequest.subject}</h3>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {getLeaveTypeBadge(selectedRequest.leave_type)}
                    {getStatusBadge(selectedRequest.status)}
                    {getSalaryBadge(selectedRequest)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 rounded-2xl bg-purple-50/80 p-4 ring-1 ring-purple-100">
                <div>
                  <p className="text-xs text-slate-500">Start Date</p>
                  <p className="font-medium text-slate-900">{formatDate(selectedRequest.start_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">End Date</p>
                  <p className="font-medium text-slate-900">{formatDate(selectedRequest.end_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Total Days</p>
                  <p className="font-medium text-slate-900">{selectedRequest.days_count} day(s)</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Submitted</p>
                  <p className="font-medium text-slate-900">
                    {formatUtcDate(selectedRequest.created_at)}
                  </p>
                </div>
              </div>

              <div>
                <p className="mb-1 text-xs text-slate-500">Reason</p>
                <p className="rounded-2xl border border-slate-200 bg-white p-3 text-slate-700">{selectedRequest.reason}</p>
              </div>

              {selectedRequest.attachment_url && (
                <div>
                  <p className="mb-1 text-xs text-slate-500">Attachment</p>
                  <a
                    href={selectedRequest.attachment_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 font-medium text-purple-700 hover:text-purple-800"
                  >
                    <Paperclip className="h-4 w-4" />
                    View Attachment
                  </a>
                </div>
              )}

              {selectedRequest.admin_remarks && (
                <div className={`rounded-2xl p-4 ring-1 ${
                  selectedRequest.status === 'approved' ? 'bg-indigo-50 ring-indigo-200' :
                  selectedRequest.status === 'rejected' ? 'bg-rose-50 ring-rose-200' :
                  'bg-slate-50 ring-slate-200'
                }`}>
                  <p className="mb-1 text-xs text-slate-500">Admin Remarks</p>
                  <p className="font-medium text-slate-900">{selectedRequest.admin_remarks}</p>
                  {selectedRequest.reviewed_at && (
                    <p className="mt-2 text-xs text-slate-500">
                      Reviewed on {formatUtcDate(selectedRequest.reviewed_at)}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 border-t border-slate-100 px-6 py-4 sm:space-x-0">
            {selectedRequest?.status === 'pending' && (
              <>
                <Button
                  variant="outline"
                  className="h-11 rounded-2xl border-rose-200 text-rose-600 hover:bg-rose-50"
                  onClick={() => openActionDialog(selectedRequest, 'reject')}
                >
                  <X className="mr-2 h-4 w-4" />
                  Reject
                </Button>
                <Button
                  className="h-11 rounded-2xl bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => openActionDialog(selectedRequest, 'approve')}
                >
                  <Check className="mr-2 h-4 w-4" />
                  Approve
                </Button>
              </>
            )}
            {selectedRequest?.status !== 'pending' && (
              <Button variant="outline" className="h-11 rounded-2xl" onClick={() => setShowDetailDialog(false)}>
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${
              actionType === 'approve' ? 'text-indigo-700' : 'text-red-700'
            }`}>
              {actionType === 'approve' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <XCircle className="w-5 h-5" />
              )}
              {actionType === 'approve' ? 'Approve' : 'Reject'} Leave Request
            </DialogTitle>
            <DialogDescription>
              {actionType === 'approve' 
                ? 'Choose whether this leave should count toward paid salary, then confirm.'
                : 'This will reject the leave request. Please provide a reason.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {selectedRequest && (
              <div className="p-3 bg-gray-50 rounded-lg text-sm">
                <p><strong>{selectedRequest.staff_name}</strong></p>
                <p className="text-gray-600">{selectedRequest.subject}</p>
                <p className="text-gray-500 mt-1">
                  {formatDate(selectedRequest.start_date)} - {formatDate(selectedRequest.end_date)} ({selectedRequest.days_count} days)
                </p>
              </div>
            )}

            {actionType === 'approve' && (
              <div className="space-y-2">
                <Label>Salary treatment *</Label>
                <div className="grid grid-cols-1 gap-2">
                  <button
                    type="button"
                    onClick={() => setIsPaid(true)}
                    data-testid="approve-paid-option"
                    className={`text-left rounded-lg border p-3 transition-colors ${
                      isPaid === true
                        ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <p className="text-sm font-semibold text-indigo-800">Approve with paid salary</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Leave days count as paid — included in base pay / no unpaid deduction.
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsPaid(false)}
                    data-testid="approve-unpaid-option"
                    className={`text-left rounded-lg border p-3 transition-colors ${
                      isPaid === false
                        ? 'border-amber-500 bg-amber-50 ring-1 ring-amber-500'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <p className="text-sm font-semibold text-amber-800">Approve with unpaid salary</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Leave days are unpaid — excluded from base pay like an absence.
                    </p>
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="admin_remarks">
                Remarks {actionType === 'reject' ? '*' : '(Optional)'}
              </Label>
              <Textarea
                id="admin_remarks"
                placeholder={actionType === 'approve' 
                  ? 'e.g., Approved. Enjoy your time off!'
                  : 'e.g., Request denied due to project deadline...'
                }
                value={adminRemarks}
                onChange={(e) => setAdminRemarks(e.target.value)}
                rows={3}
                data-testid="admin-remarks-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAction}
              disabled={
                processing
                || (actionType === 'reject' && !adminRemarks)
                || (actionType === 'approve' && isPaid === null)
              }
              className={actionType === 'approve' 
                ? 'bg-indigo-600 hover:bg-indigo-700' 
                : 'bg-red-600 hover:bg-red-700'
              }
              data-testid="confirm-action-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {actionType === 'approve' ? <Check className="w-4 h-4 mr-2" /> : <X className="w-4 h-4 mr-2" />}
                  {actionType === 'approve'
                    ? `Confirm ${isPaid === false ? 'Unpaid' : isPaid === true ? 'Paid' : ''} Approval`.replace(/\s+/g, ' ').trim()
                    : 'Confirm Rejection'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}

export default AdminLeaveManagementPage;
